import { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator";
import { AccountingEvent, AccountingEvents } from "../types/events";
import { PAYMENT_METHODS, UPI_COUNTRY_TO_PAYMENT_METHOD } from "../constants";
import { mapEmpProviderToPaymentMethod } from "./PaymentUtils";
import { getCountryCodeFromMarketplace } from "./marketplaceUtils";

export const isTvodAccountingEvent = (event: AccountingEvent) => {
  return Number(event.glProductGroup) === 318;
};

// Classic-flow settlement is a distinct SETTLEMENT-type accounting event
// (keyed <auid>#SETTLEMENT) carrying settlementClientId / paymentMethodChargeIds.
export const hasSettlementDetails = (accountingEvents: AccountingEvents) =>
  Object.values(accountingEvents || {}).some(
    (event) => event.type === "SETTLEMENT"
  );

const createState = (status: StatusIndicatorProps.Type, text: string) => ({
  status,
  text,
});

export const getPaymentMethod = (event: AccountingEvent): string => {
  if (event.isEmpOrder) {
    if (event.empProviderId) {
      return mapEmpProviderToPaymentMethod(event.empProviderId);
    }
    return PAYMENT_METHODS.EMP;
  }

  const paymentId = Object.keys(event.paymentInstruments || {})[0];
  const payment = event.paymentInstruments?.[paymentId];
  switch (payment?.instrument) {
    case "BankAccountPaymentInstrument":
      return PAYMENT_METHODS.DIRECT_DEBIT;
    case "CreditCardPaymentInstrument":
      return PAYMENT_METHODS.CREDIT_CARD;
    case "UnmappedPaymentInstrument": {
      const country = getCountryCodeFromMarketplace(event.marketplaceId);
      return UPI_COUNTRY_TO_PAYMENT_METHOD[country] ?? payment.instrument;
    }
    default:
      return payment?.instrument ?? PAYMENT_METHODS.AMP;
  }
};

export const getPaymentState = (event: AccountingEvent) => {
  const method = getPaymentMethod(event);
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (!method) return createState("error", "No Payment Data");
  if (event.isFreeTrial)
    return createState("in-progress", `Free Trial (${method})`);
  if (events.includes("OrderSettleSucceeded"))
    return createState("success", `Paid (${method})`);
  if (events.includes("OrderCompleted-ClosedNonPayment"))
    return createState("error", `Failed (${method})`);
  return createState("in-progress", `Pending (${method})`);
};

export const getFulfillmentState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (
    !isTvodAccountingEvent(event) &&
    !(events.includes("PeriodStart") || events.includes("Resubscribed"))
  ) {
    return createState("error", "No Period Start");
  }
  if (events.includes("Cancelled")) return createState("error", "Cancelled");
  if (events.includes("Suspended")) return createState("error", "Suspended");
  if (
    events.includes("PeriodStart") ||
    events.includes("Resubscribed") ||
    (isTvodAccountingEvent(event) && events.includes("OrderFulfilled"))
  ) {
    return createState("success", "Fulfilled");
  }

  return createState("in-progress", "Pending");
};

export const getRefundState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (events.includes("RefundSucceeded"))
    return createState("success", "Refunded");
  if (events.includes("RefundFailed"))
    return createState("error", "Refund Failed");
  if (events.includes("ReturnCompleted-FreeReturn"))
    return createState("success", "Free Return");
  return createState("in-progress", "No Refund");
};
export const getEMPSubsOrchestrationPlatform = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (events.includes("PeriodStart")) {
    return event.empPurchaseHandleId?.trim() ? "Thunderbird" : "Denarius";
  }
  return "Unknown (No PeriodStart)";
};
